import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  List,
  ListItem,
  ListItemText,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListScopeItemsQuery,
  useUpsertScopeItemMutation,
  useDeleteScopeItemMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { ScopeItem } from '../types/models';

interface FormData {
  content: string;
}

export default function ScopePage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const { data: items = [], isLoading } = useListScopeItemsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const [upsert] = useUpsertScopeItemMutation();
  const [remove] = useDeleteScopeItemMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ScopeItem | null>(null);
  const [toDelete, setToDelete] = useState<ScopeItem | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Sprint Scope" />
        <Alert severity="info">Select a project and sprint.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({ content: '' });
    setOpen(true);
  };

  const openEdit = (s: ScopeItem) => {
    setEditing(s);
    reset({ content: s.content });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await upsert({ uid, projectId, sprintId, id: editing?.id, ...data }).unwrap();
      dispatch(showToast({ message: editing ? 'Updated' : 'Added', severity: 'success' }));
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, sprintId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Sprint Scope"
        subtitle="Free-form notes & bullet points describing the scope."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New scope item
          </Button>
        }
      />

      {!isLoading && items.length === 0 ? (
        <EmptyState
          title="No scope items"
          description="Add notes, bullet lists or markdown blocks describing the sprint scope."
          action={<Button variant="contained" onClick={openCreate}>Add item</Button>}
        />
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {items.map((s) => (
              <ListItem
                key={s.id}
                divider
                alignItems="flex-start"
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => openEdit(s)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setToDelete(s)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        m: 0,
                      }}
                    >
                      {s.content}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit scope' : 'New scope item'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Content (supports bullets / markdown)"
                multiline
                rows={10}
                autoFocus
                placeholder={'- First bullet\n- Second bullet\n\n**Notes:** something here'}
                error={!!errors.content}
                helperText={errors.content?.message}
                {...register('content', { required: 'Required' })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete scope item?"
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
